import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import {
    Card, Button, Table, Modal, Input, Select,
    message, Popconfirm, Space, Tag, Row, Col, Badge, Tooltip, Switch, Alert
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    BookOutlined, ApartmentOutlined, UnorderedListOutlined,
    InfoCircleOutlined, SettingOutlined, CheckSquareOutlined,
    ArrowUpOutlined, ArrowDownOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { InstructionsButton } from './QoCInstructions';
import { trailCreate, trailUpdate, trailDelete, TRAIL_MODULES } from '../../utils/activityTrail';
const { TextArea } = Input;

const CLAUSE_COLORS = {
    AACC:'#0ea5e9', COP:'#8b5cf6', MOM:'#f59e0b', PRPE:'#10b981',
    IPCM:'#ef4444', PSQI:'#3b82f6', GROM:'#6366f1', FMS:'#ec4899',
    HRE:'#14b8a6', ISM:'#a855f7',
};

const STANDARD_ACCENTS = [
    '#6366f1','#8b5cf6','#a855f7','#3b82f6','#0ea5e9','#06b6d4',
    '#10b981','#14b8a6','#22c55e','#84cc16',
    '#f59e0b','#f97316','#ef4444','#e11d48',
    '#ec4899','#db2777','#c026d3',
    '#78716c','#64748b','#475569',
    '#0891b2','#059669','#d97706','#dc2626',
];

const SERVICE_OPTIONS = [
    { value: '',              label: 'Universal (all facilities)' },
    { value: 'OPD',          label: 'OPD' },
    { value: 'ANC',          label: 'ANC' },
    { value: 'ARV',          label: 'ARV / HIV' },
    { value: 'TB',           label: 'TB Treatment' },
    { value: 'Maternity',    label: 'MCH / Maternity' },
    { value: 'Immunisation', label: 'Immunisation' },
    { value: 'Lab',          label: 'Laboratory' },
    { value: 'Radiology',    label: 'Radiology / X-Ray' },
    { value: 'Pharmacy',     label: 'Pharmacy' },
    { value: 'Emergency',    label: 'Emergency / Casualty' },
    { value: 'Inpatient',    label: 'Inpatient Wards' },
    { value: 'Surgery',      label: 'Surgery / Theatre' },
    { value: 'Mental Health',label: 'Mental Health' },
    { value: 'Dental',       label: 'Dental' },
    { value: 'Nutrition',    label: 'Nutrition' },
    { value: 'Physiotherapy',label: 'Physiotherapy' },
    { value: 'Palliative',   label: 'Palliative Care' },
];

const SERVICE_COLORS = {
    'OPD':'blue','ANC':'pink','ARV':'green','TB':'orange',
    'Maternity':'magenta','Immunisation':'cyan','Lab':'gold',
    'Radiology':'geekblue','Pharmacy':'lime','Emergency':'red',
    'Inpatient':'purple','Surgery':'volcano','Mental Health':'blue',
    'Dental':'cyan','Nutrition':'green','Physiotherapy':'geekblue',
    'Palliative':'gray',
};

const serviceLabel = (val) =>
    SERVICE_OPTIONS.find(o => o.value === val)?.label || val || 'Universal';

const panelCard = () => ({
    height: 'calc(100vh - 180px)',
    display: 'flex',
    flexDirection: 'column',
});

const scrollBody = {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 0',
};

const rowItem = (active, accent) => ({
    padding: '10px 12px',
    cursor: 'pointer',
    borderLeft: `3px solid ${active ? (accent || '#1890ff') : 'transparent'}`,
    background: active ? 'rgba(24,144,255,0.06)' : 'transparent',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
});

const Field = ({ label, children, hint }) => (
    <div style={{ marginBottom: 14 }}>
        <label style={{ fontWeight: 600, fontSize: 12, display: 'block', marginBottom: 4, color: '#555' }}>
            {label}
        </label>
        {children}
        {hint && <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>{hint}</div>}
    </div>
);

export default function QoCStandardSetup() {
    const [standards,      setStandards]      = useState([]);
    const [selectedStd,    setSelectedStd]    = useState(null);
    const [clauses,        setClauses]        = useState([]);
    const [selectedClause, setSelectedClause] = useState(null);
    // Permission checks
    const canAdd    = util.canAdd('qoc_standards');
    const canEdit   = util.canEdit('qoc_standards');
    const canDelete = util.canDelete('qoc_standards');
    const canExport = util.canExport('qoc_standards');
    const [subStandards,   setSubStandards]   = useState([]);
    const [selectedSub,    setSelectedSub]    = useState(null);
    const [questions,      setQuestions]      = useState([]);
    const [loading,        setLoading]        = useState(false);

    const [stdModal,    setStdModal]    = useState(false);
    const [stdForm,     setStdForm]     = useState({});
    const [clauseModal, setClauseModal] = useState(false);
    const [clauseForm,  setClauseForm]  = useState({});
    const [subModal,    setSubModal]    = useState(false);
    const [subForm,     setSubForm]     = useState({});
    const [meModal,     setMeModal]     = useState(false);
    const [meForm,      setMeForm]      = useState({});
    const [viewModal,   setViewModal]   = useState(false);

    // ── Checklist editor state ──────────────────────────────────────────────
    const [checklistModal,    setChecklistModal]    = useState(false);
    const [checklistMe,       setChecklistMe]       = useState(null);
    const [checklistItems,    setChecklistItems]    = useState([]); // [{ item_text }]
    const [checklistReviewed, setChecklistReviewed] = useState(false);
    const [checklistSaving,   setChecklistSaving]   = useState(false);

    useEffect(() => { loadStandards(); }, []);

    // Depend on .id (primitive) so switching standards always re-fires
    useEffect(() => {
        if (!selectedStd?.id) { setClauses([]); setSelectedClause(null); return; }
        setClauses([]); setSelectedClause(null); setSelectedSub(null); setQuestions([]);
        loadClauses(selectedStd.id);
    }, [selectedStd?.id]);

    useEffect(() => {
        if (!selectedClause?.id) { setSubStandards([]); setSelectedSub(null); return; }
        setSubStandards([]); setSelectedSub(null); setQuestions([]);
        loadSubStandards(selectedClause.id);
    }, [selectedClause?.id]);

    useEffect(() => {
        if (!selectedSub?.id) { setQuestions([]); return; }
        setQuestions([]);
        loadMEs(selectedSub.id);
    }, [selectedSub?.id]);

    const loadStandards = async (keepSelection = false) => {
        setLoading(true);
        try {
            const res = await QoCService.getStandards();
            const list = res.data?.data || [];
            setStandards(list);
            if (list.length > 0 && !keepSelection) {
                // Auto-select first standard only on initial load
                setSelectedStd(prev => prev ? list.find(s => s.id === prev.id) || prev : list[0]);
            }
        } catch { message.error('Failed to load standards'); }
        finally { setLoading(false); }
    };

    const loadClauses = async (standardId) => {
        setLoading(true);
        try {
            const res = await QoCService.getClauses({ standard_id: standardId });
            setClauses(res.data?.data || []);
        } catch { message.error('Failed to load clauses'); }
        finally { setLoading(false); }
    };

    const loadSubStandards = async (clauseId) => {
        setLoading(true);
        try {
            const res = await QoCService.getSubStandards(clauseId);
            setSubStandards(res.data?.data || []);
        } catch { message.error('Failed to load sub-standards'); }
        finally { setLoading(false); }
    };

    const loadMEs = async (subId) => {
        setLoading(true);
        try {
            // Try qoc_measurable_elements first (new schema)
            const res = await QoCService.getMeasurableElements({ sub_standard_id: subId });
            const mes = res.data?.data || [];
            if (mes.length > 0) {
                setQuestions(mes);
            } else {
                // Fall back to qoc_questions (legacy QoC Tool MEs)
                const res2 = await QoCService.getQuestions(subId);
                setQuestions(res2.data?.data || []);
            }
        } catch { message.error('Failed to load measurable elements'); }
        finally { setLoading(false); }
    };

    const openStdModal = (std = null) => {
        setStdForm(std
            ? { ...std }
            : { name: '', description: '', category: '', status: 1, accent: STANDARD_ACCENTS[standards.length % STANDARD_ACCENTS.length] }
        );
        setStdModal(true);
    };

    const saveStd = async () => {
        if (!stdForm.name?.trim()) { message.error('Name is required'); return; }
        setLoading(true);
        try {
            const saved = await QoCService.saveStandard(stdForm);
            stdForm.id
                ? trailUpdate(TRAIL_MODULES.STANDARDS, `Standard: ${stdForm.name}`, stdForm.id, {})
                : trailCreate(TRAIL_MODULES.STANDARDS, `Standard: ${stdForm.name}`, '', {});
            message.success('Standard saved');
            setStdModal(false);
            loadStandards(!!stdForm.id); // keep selection when editing, release when creating
        } catch { message.error('Failed to save standard'); }
        finally { setLoading(false); }
    };

    const deleteStd = async (id) => {
        try {
            await QoCService.deleteStandard(id);
            trailDelete(TRAIL_MODULES.STANDARDS, `Standard #${id}`, id, {});
            message.success('Standard deleted');
            if (selectedStd?.id === id) setSelectedStd(null);
            loadStandards();
        } catch { message.error('Failed to delete standard'); }
    };

    const openClauseModal = (clause = null) => {
        setClauseForm(clause
            ? { ...clause }
            : { standard_id: selectedStd?.id, code: '', title: '', clause_number: '', service_tag: null, active: 1 }
        );
        setClauseModal(true);
    };

    const saveClause = async () => {
        if (!clauseForm.title?.trim()) { message.error('Title is required'); return; }
        if (!clauseForm.code?.trim())  { message.error('Code is required');  return; }
        setLoading(true);
        try {
            await QoCService.saveClause({ ...clauseForm, service_tag: clauseForm.service_tag || null, standard_id: selectedStd?.id });
            clauseForm.id
                ? trailUpdate(TRAIL_MODULES.STANDARDS, `Clause: ${clauseForm.code}`, clauseForm.id, { standard: selectedStd?.name })
                : trailCreate(TRAIL_MODULES.STANDARDS, `Clause: ${clauseForm.code}`, '', { standard: selectedStd?.name });
            message.success('Clause saved');
            setClauseModal(false);
            loadClauses(selectedStd.id);
        } catch { message.error('Failed to save clause'); }
        finally { setLoading(false); }
    };

    const deleteClause = async (id) => {
        try {
            await QoCService.deleteClause(id);
            trailDelete(TRAIL_MODULES.STANDARDS, `Clause #${id}`, id, { standard: selectedStd?.name });
            message.success('Clause deleted');
            if (selectedClause?.id === id) setSelectedClause(null);
            loadClauses(selectedStd.id);
        } catch { message.error('Failed to delete clause'); }
    };

    const openSubModal = (sub = null) => {
        setSubForm(sub || { clause_id: selectedClause?.id, standard_id: selectedStd?.id || 1, service_tag: null, status: 1 });
        setSubModal(true);
    };

    const saveSub = async () => {
        if (!subForm.name?.trim()) { message.error('Name is required'); return; }
        setLoading(true);
        try {
            await QoCService.saveSubStandard({ ...subForm, standard_id: selectedStd?.id || 1, service_tag: subForm.service_tag || null });
            subForm.id
                ? trailUpdate(TRAIL_MODULES.STANDARDS, `Sub-standard: ${subForm.name}`, subForm.id, { clause: selectedClause?.code })
                : trailCreate(TRAIL_MODULES.STANDARDS, `Sub-standard: ${subForm.name}`, '', { clause: selectedClause?.code });
            message.success('Sub-standard saved');
            setSubModal(false);
            loadSubStandards(selectedClause.id);
        } catch { message.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const deleteSub = async (id) => {
        try {
            await QoCService.deleteSubStandard(id);
            trailDelete(TRAIL_MODULES.STANDARDS, `Sub-standard #${id}`, id, { clause: selectedClause?.code });
            message.success('Sub-standard deleted');
            if (selectedSub?.id === id) setSelectedSub(null);
            loadSubStandards(selectedClause.id);
        } catch { message.error('Failed to delete'); }
    };

    const openMeModal = (me = null) => {
        setMeForm(me || {
            sub_standard_id: selectedSub?.id, clause_id: selectedClause?.id,
            question_type: 'yes_no', verification_means: 'observation',
            scoring_rule: 'binary', max_score: 1, status: 1,
        });
        setMeModal(true);
    };

    const saveMe = async () => {
        if (!meForm.question_text?.trim()) { message.error('ME text is required'); return; }
        setLoading(true);
        try {
            // Pass clause_id and ref_no explicitly so they survive the model's field list
            const mePayload = {
                ...meForm,
                sub_standard_id: selectedSub.id,
                clause_id:        selectedClause?.id,
                ref_no:           meForm.ref_no || '',
                me_text:          meForm.question_text, // some model versions use me_text
            };
            await QoCService.saveBulkQuestions({ questions: [mePayload] });
            meForm.id
                ? trailUpdate(TRAIL_MODULES.STANDARDS, `ME: ${meForm.ref_no || meForm.question_text?.slice(0,40)}`, meForm.id, { clause: selectedClause?.code })
                : trailCreate(TRAIL_MODULES.STANDARDS, `ME: ${meForm.ref_no || 'new'}`, '', { clause: selectedClause?.code });
            message.success('Measurable element saved');
            setMeModal(false);
            loadMEs(selectedSub.id);
        } catch { message.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const deleteMe = async (id) => {
        try {
            await QoCService.deleteQuestion(id);
            trailDelete(TRAIL_MODULES.STANDARDS, `ME #${id}`, id, { clause: selectedClause?.code });
            message.success('ME deleted');
            loadMEs(selectedSub.id);
        } catch { message.error('Failed to delete'); }
    };

    // ── Checklist editor ──────────────────────────────────────────────────────
    const openChecklistModal = (me) => {
        setChecklistMe(me);
        const items = (me.checklist_items || []).map(it => ({ id: it.id, item_text: it.item_text }));
        setChecklistItems(items.length > 0 ? items : [{ item_text: '' }]);
        setChecklistReviewed(!!me.checklist_reviewed);
        setChecklistModal(true);
    };

    const addChecklistItem = () => setChecklistItems(prev => [...prev, { item_text: '' }]);

    const updateChecklistItem = (idx, text) =>
        setChecklistItems(prev => prev.map((it, i) => i === idx ? { ...it, item_text: text } : it));

    const removeChecklistItem = (idx) =>
        setChecklistItems(prev => prev.filter((_, i) => i !== idx));

    const moveChecklistItem = (idx, dir) => {
        setChecklistItems(prev => {
            const next = [...prev];
            const target = idx + dir;
            if (target < 0 || target >= next.length) return prev;
            [next[idx], next[target]] = [next[target], next[idx]];
            return next;
        });
    };

    const saveChecklist = async () => {
        if (!checklistMe?.id) return;
        const cleanItems = checklistItems
            .map(it => ({ item_text: (it.item_text || '').trim() }))
            .filter(it => it.item_text);
        setChecklistSaving(true);
        try {
            await QoCService.saveMeChecklist(checklistMe.id, {
                items: cleanItems,
                reviewed: checklistReviewed ? 1 : 0,
            });
            trailUpdate(TRAIL_MODULES.STANDARDS, `Checklist: ${checklistMe.ref_no || checklistMe.id}`,
                checklistMe.id, { clause: selectedClause?.code, item_count: cleanItems.length, reviewed: checklistReviewed });
            message.success('Checklist saved');
            setChecklistModal(false);
            loadMEs(selectedSub.id);
        } catch (e) {
            message.error('Failed to save checklist');
        } finally {
            setChecklistSaving(false);
        }
    };

    const stdAccent    = selectedStd?.accent || '#1890ff';
    const clauseAccent = selectedClause?.code ? (CLAUSE_COLORS[selectedClause.code] || stdAccent) : stdAccent;

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto"><h2>Standards Setup</h2></div>
                <div className="my-auto ml-auto"><InstructionsButton /></div>
            </div>

            <div className="page-pad">
                <Row gutter={10} style={{ flexWrap: 'nowrap' }}>

                    {/* Panel 1 — Standards */}
                    <Col style={{ width: 200, flexShrink: 0 }}>
                        <Card size="small" style={panelCard()}
                            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
                            title={<span style={{ fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 1 }}>STANDARDS</span>}>
                            <div style={scrollBody}>
                                {standards.map((std, i) => {
                                    const accent = std.accent || STANDARD_ACCENTS[i % STANDARD_ACCENTS.length];
                                    const active = selectedStd?.id === std.id;
                                    return (
                                        <div key={std.id}
                                            onClick={() => { setSelectedStd(std); setSelectedClause(null); setSelectedSub(null); }}
                                            style={rowItem(active, accent)}>
                                            <div style={{ width: 28, height: 28, borderRadius: 7, background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                                                {std.name?.charAt(0) || 'S'}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: active ? 700 : 600, fontSize: 12, color: active ? accent : '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{std.name}</div>
                                                {std.category && <div style={{ fontSize: 10, color: '#bbb', marginTop: 1 }}>{std.category}</div>}
                                            </div>
                                            {canEdit && <Tooltip title="Edit">
                                                <Button size="small" type="text" icon={<SettingOutlined />} style={{ flexShrink: 0, opacity: 0.4 }}
                                                    onClick={e => { e.stopPropagation(); openStdModal(std); }} />
                                            </Tooltip>}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ padding: '8px 10px', borderTop: '1px solid #f0f0f0' }}>
                                {canAdd && <Button block size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openStdModal()}>
                                    New standard
                                </Button>}
                            </div>
                        </Card>
                    </Col>

                    {/* Panel 2 — Clauses */}
                    <Col style={{ width: 200, flexShrink: 0 }}>
                        <Card size="small" style={panelCard()}
                            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
                            title={selectedStd
                                ? <span style={{ fontSize: 11, fontWeight: 700, color: stdAccent, letterSpacing: 1 }}>{selectedStd.name.toUpperCase()} — CLAUSES</span>
                                : <span style={{ fontSize: 11, color: '#ccc', letterSpacing: 1 }}>SELECT STANDARD →</span>}>
                            {selectedStd ? (
                                <>
                                    <div style={scrollBody}>
                                        {clauses.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#ccc' }}>
                                                <ApartmentOutlined style={{ fontSize: 28 }} />
                                                <div style={{ marginTop: 8, fontSize: 12 }}>No clauses yet</div>
                                            </div>
                                        )}
                                        {clauses.map(c => {
                                            const accent = CLAUSE_COLORS[c.code] || stdAccent;
                                            const active = selectedClause?.id === c.id;
                                            return (
                                                <div key={c.id}
                                                    onClick={() => { setSelectedClause(c); setSelectedSub(null); }}
                                                    style={rowItem(active, accent)}>
                                                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                                                        {c.clause_number || c.code?.slice(0,2)}
                                                    </div>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: active ? 700 : 600, fontSize: 12, color: active ? accent : '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.code}</div>
                                                        <div style={{ fontSize: 10, color: '#aaa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.title}</div>
                                                        {c.service_tag && (
                                                            <Tag color={SERVICE_COLORS[c.service_tag] || 'default'} style={{ fontSize: 9, padding: '0 4px', marginTop: 2 }}>
                                                                {serviceLabel(c.service_tag)}
                                                            </Tag>
                                                        )}
                                                    </div>
                                                    {canEdit && <Button size="small" type="text" icon={<EditOutlined />} style={{ flexShrink: 0, opacity: 0.4 }}
                                                        onClick={e => { e.stopPropagation(); openClauseModal(c); }} />}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div style={{ padding: '8px 10px', borderTop: '1px solid #f0f0f0' }}>
                                        {canAdd && <Button block size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openClauseModal()}>
                                            New clause
                                        </Button>}
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 16px', color: '#ccc' }}>
                                    <BookOutlined style={{ fontSize: 28 }} />
                                    <div style={{ marginTop: 8, fontSize: 12 }}>Select a standard first</div>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Panel 3 — Sub-standards */}
                    <Col style={{ width: 220, flexShrink: 0 }}>
                        <Card size="small" style={panelCard()}
                            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
                            title={selectedClause
                                ? <span style={{ fontSize: 11, fontWeight: 700, color: clauseAccent, letterSpacing: 1 }}>{selectedClause.code} — SUB-STANDARDS</span>
                                : <span style={{ fontSize: 11, color: '#ccc', letterSpacing: 1 }}>SELECT CLAUSE →</span>}>
                            {selectedClause ? (
                                <>
                                    <div style={scrollBody}>
                                        {subStandards.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#ccc' }}>
                                                <UnorderedListOutlined style={{ fontSize: 28 }} />
                                                <div style={{ marginTop: 8, fontSize: 12 }}>No sub-standards yet</div>
                                            </div>
                                        )}
                                        {subStandards.map(sub => {
                                            const active = selectedSub?.id === sub.id;
                                            return (
                                                <div key={sub.id}
                                                    onClick={() => setSelectedSub(sub)}
                                                    style={rowItem(active, clauseAccent)}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: active ? 700 : 600, fontSize: 12, color: active ? clauseAccent : '#333' }}>{sub.name}</div>
                                                        <Space size={3} style={{ marginTop: 3, flexWrap: 'wrap' }}>
                                                            {sub.ref_prefix && <Tag color="blue" style={{ fontSize: 9, padding: '0 4px', margin: 0 }}>{sub.ref_prefix}</Tag>}
                                                            {sub.service_tag
                                                                ? <Tag color={SERVICE_COLORS[sub.service_tag] || 'default'} style={{ fontSize: 9, padding: '0 4px', margin: 0 }}>{serviceLabel(sub.service_tag)}</Tag>
                                                                : <Tag style={{ fontSize: 9, padding: '0 4px', margin: 0, color: '#ccc', borderColor: '#eee', background: '#fafafa' }}>Universal</Tag>
                                                            }
                                                            <Badge count={sub.me_count || 0} style={{ backgroundColor: '#52c41a', fontSize: 9 }} showZero />
                                                        </Space>
                                                    </div>
                                                    <Space size={2} onClick={e => e.stopPropagation()}>
                                                        {canEdit && <Button size="small" type="text" icon={<EditOutlined />} style={{ opacity: 0.4 }} onClick={() => openSubModal(sub)} />}
                                                        {canDelete && <Popconfirm title="Delete this sub-standard?" onConfirm={() => deleteSub(sub.id)}>
                                                            <Button size="small" type="text" danger icon={<DeleteOutlined />} style={{ opacity: 0.4 }} />
                                                        </Popconfirm>}
                                                    </Space>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div style={{ padding: '8px 10px', borderTop: '1px solid #f0f0f0' }}>
                                        {canAdd && <Button block size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openSubModal()}>
                                            New sub-standard
                                        </Button>}
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 16px', color: '#ccc' }}>
                                    <UnorderedListOutlined style={{ fontSize: 28 }} />
                                    <div style={{ marginTop: 8, fontSize: 12 }}>Select a clause first</div>
                                </div>
                            )}
                        </Card>
                    </Col>

                    {/* Panel 4 — Measurable elements */}
                    <Col style={{ flex: 1, minWidth: 0 }}>
                        <Card size="small" style={panelCard()}
                            bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}
                            title={selectedSub
                                ? <span style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 1 }}>MEs — <span style={{ fontWeight: 400 }}>{selectedSub.name}</span></span>
                                : <span style={{ fontSize: 11, color: '#ccc', letterSpacing: 1 }}>SELECT SUB-STANDARD →</span>}>
                            {selectedSub ? (
                                <>
                                    <div style={{ ...scrollBody, padding: '6px 10px' }}>
                                        {questions.length === 0 && (
                                            <div style={{ textAlign: 'center', padding: '32px 16px', color: '#ccc' }}>
                                                <InfoCircleOutlined style={{ fontSize: 28 }} />
                                                <div style={{ marginTop: 8, fontSize: 12 }}>No MEs yet</div>
                                            </div>
                                        )}
                                        {questions.map(q => {
                                            const clItems = q.checklist_items || [];
                                            const clCount = clItems.length;
                                            const clReviewed = !!q.checklist_reviewed;
                                            return (
                                            <div key={q.id} style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid #f0f0f0', marginBottom: 6, background: '#fafafa' }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <Space size={6} style={{ marginBottom: 5, flexWrap: 'wrap' }}>
                                                            {q.ref_no && (
                                                                <Tag style={{ fontFamily: 'monospace', fontSize: 11, background: clauseAccent, color: '#fff', border: 'none' }}>{q.ref_no}</Tag>
                                                            )}
                                                            {q.verification_means && <Tag style={{ fontSize: 10 }}>{q.verification_means}</Tag>}
                                                            {q.scoring_rule && <Tag color="purple" style={{ fontSize: 10 }}>{q.scoring_rule}</Tag>}
                                                            {clCount === 0 && (
                                                                <Tag style={{ fontSize: 10, color: '#bbb', borderColor: '#eee', background: '#fafafa' }}>No checklist</Tag>
                                                            )}
                                                            {clCount > 0 && clReviewed && (
                                                                <Tag color="green" style={{ fontSize: 10 }}>✓ {clCount} item{clCount!==1?'s':''} reviewed</Tag>
                                                            )}
                                                            {clCount > 0 && !clReviewed && (
                                                                <Tag color="orange" style={{ fontSize: 10 }}>⚠ {clCount} item{clCount!==1?'s':''} needs review</Tag>
                                                            )}
                                                        </Space>
                                                        <div style={{ fontSize: 12, color: '#333', lineHeight: 1.5 }}>
                                                            {((q.me_text || q.question_text) || '')?.length > 160 ? (q.me_text || q.question_text).substring(0, 160) + '…' : (q.me_text || q.question_text)}
                                                        </div>
                                                    </div>
                                                    <Space size={4} style={{ flexShrink: 0 }}>
                                                        <Button size="small" icon={<InfoCircleOutlined />} onClick={() => { setMeForm(q); setViewModal(true); }}>View</Button>
                                                        {canEdit && (
                                                            <Tooltip title="Edit checklist">
                                                                <Button size="small" icon={<CheckSquareOutlined />}
                                                                    style={clCount > 0 && !clReviewed ? { borderColor:'#fa8c16', color:'#fa8c16' } : undefined}
                                                                    onClick={() => openChecklistModal(q)} />
                                                            </Tooltip>
                                                        )}
                                                        {canEdit && <Button size="small" icon={<EditOutlined />} onClick={() => openMeModal(q)} />}
                                                        {canDelete && <Popconfirm title="Delete this ME?" onConfirm={() => deleteMe(q.id)}>
                                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                                        </Popconfirm>}
                                                    </Space>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                    <div style={{ padding: '8px 10px', borderTop: '1px solid #f0f0f0' }}>
                                        {canAdd && <Button block size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openMeModal()}>
                                            New measurable element
                                        </Button>}
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 16px', color: '#ccc' }}>
                                    <UnorderedListOutlined style={{ fontSize: 28 }} />
                                    <div style={{ marginTop: 8, fontSize: 12 }}>Select a sub-standard first</div>
                                </div>
                            )}
                        </Card>
                    </Col>

                </Row>
            </div>

            {/* Standard modal */}
            <Modal title={stdForm.id ? 'Edit Standard' : 'New Standard'} open={stdModal} onCancel={() => setStdModal(false)} onOk={saveStd} confirmLoading={loading} okText="Save" width={480}>
                <Field label="Name *">
                    <Input value={stdForm.name} onChange={e => setStdForm({ ...stdForm, name: e.target.value })} placeholder="e.g. TB Programme Quality Standard" />
                </Field>
                <Field label="Description">
                    <TextArea rows={2} value={stdForm.description} onChange={e => setStdForm({ ...stdForm, description: e.target.value })} />
                </Field>
                <Field label="Category">
                    <Input value={stdForm.category} onChange={e => setStdForm({ ...stdForm, category: e.target.value })} placeholder="e.g. National, Programme, Facility-specific" />
                </Field>
                <Field label="Accent colour" hint="Identifies this standard across the UI">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {STANDARD_ACCENTS.map(c => (
                            <div key={c} onClick={() => setStdForm({ ...stdForm, accent: c })}
                                style={{ width: 28, height: 28, borderRadius: 7, background: c, cursor: 'pointer', outline: stdForm.accent === c ? `3px solid ${c}` : '3px solid transparent', outlineOffset: 2, transition: 'outline 0.15s' }} />
                        ))}
                    </div>
                </Field>
                {stdForm.id && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f5f5f5' }}>
                        <Popconfirm title="Delete this standard and all its clauses?" onConfirm={() => { deleteStd(stdForm.id); setStdModal(false); }}>
                            <Button danger size="small">Delete standard</Button>
                        </Popconfirm>
                    </div>
                )}
            </Modal>

            {/* Clause modal */}
            <Modal title={clauseForm.id ? 'Edit Clause' : 'New Clause'} open={clauseModal} onCancel={() => setClauseModal(false)} onOk={saveClause} confirmLoading={loading} okText="Save" width={520}>
                <Row gutter={12}>
                    <Col span={8}>
                        <Field label="Code *">
                            <Input value={clauseForm.code} onChange={e => setClauseForm({ ...clauseForm, code: e.target.value.toUpperCase() })} placeholder="e.g. AACC" />
                        </Field>
                    </Col>
                    <Col span={4}>
                        <Field label="No.">
                            <Input value={clauseForm.clause_number} onChange={e => setClauseForm({ ...clauseForm, clause_number: e.target.value })} placeholder="1" />
                        </Field>
                    </Col>
                    <Col span={12}>
                        <Field label="Title *">
                            <Input value={clauseForm.title} onChange={e => setClauseForm({ ...clauseForm, title: e.target.value })} />
                        </Field>
                    </Col>
                </Row>
                <Field label="Applies to service" hint="When set, this clause only appears for facilities offering this service.">
                    <Select value={clauseForm.service_tag || ''} onChange={v => setClauseForm({ ...clauseForm, service_tag: v || null })} style={{ width: '100%' }}>
                        {SERVICE_OPTIONS.map(opt => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.value ? <Tag color={SERVICE_COLORS[opt.value]} style={{ margin: 0 }}>{opt.label}</Tag> : <span style={{ color: '#aaa' }}>{opt.label}</span>}
                            </Select.Option>
                        ))}
                    </Select>
                </Field>
                {clauseForm.id && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f5f5f5' }}>
                        <Popconfirm title="Delete this clause?" onConfirm={() => { deleteClause(clauseForm.id); setClauseModal(false); }}>
                            <Button danger size="small">Delete clause</Button>
                        </Popconfirm>
                    </div>
                )}
            </Modal>

            {/* Sub-standard modal */}
            <Modal title={subForm.id ? 'Edit Sub-Standard' : 'New Sub-Standard'} open={subModal} onCancel={() => setSubModal(false)} onOk={saveSub} confirmLoading={loading} okText="Save" width={520}>
                <Field label="Name *">
                    <Input value={subForm.name} onChange={e => setSubForm({ ...subForm, name: e.target.value })} />
                </Field>
                <Field label="Ref prefix">
                    <Input value={subForm.ref_prefix} onChange={e => setSubForm({ ...subForm, ref_prefix: e.target.value })} placeholder="e.g. AACC.1" />
                </Field>
                <Field label="Applies to service" hint="All MEs under this sub-standard only appear for facilities offering this service.">
                    <Select value={subForm.service_tag || ''} onChange={v => setSubForm({ ...subForm, service_tag: v || null })} style={{ width: '100%' }}>
                        {SERVICE_OPTIONS.map(opt => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.value ? <Tag color={SERVICE_COLORS[opt.value]} style={{ margin: 0 }}>{opt.label}</Tag> : <span style={{ color: '#aaa' }}>{opt.label}</span>}
                            </Select.Option>
                        ))}
                    </Select>
                </Field>
                <Field label="Description">
                    <TextArea rows={2} value={subForm.description} onChange={e => setSubForm({ ...subForm, description: e.target.value })} />
                </Field>
            </Modal>

            {/* ME edit modal */}
            <Modal title={meForm.id ? 'Edit Measurable Element' : 'New Measurable Element'} open={meModal} onCancel={() => setMeModal(false)} onOk={saveMe} confirmLoading={loading} okText="Save" width={640}>
                <Row gutter={12}>
                    <Col span={8}>
                        <Field label="Ref No">
                            <Input value={meForm.ref_no} onChange={e => setMeForm({ ...meForm, ref_no: e.target.value })} placeholder="e.g. AACC.1.1" />
                        </Field>
                    </Col>
                    <Col span={8}>
                        <Field label="Verification means">
                            <Select value={meForm.verification_means} onChange={v => setMeForm({ ...meForm, verification_means: v })} style={{ width: '100%' }}>
                                {['document','observation','interview','physical_check','mixed'].map(v => <Select.Option key={v} value={v}>{v}</Select.Option>)}
                            </Select>
                        </Field>
                    </Col>
                    <Col span={8}>
                        <Field label="Scoring rule">
                            <Select value={meForm.scoring_rule} onChange={v => setMeForm({ ...meForm, scoring_rule: v })} style={{ width: '100%' }}>
                                {['binary','partial','weighted','measurable_element'].map(v => <Select.Option key={v} value={v}>{v}</Select.Option>)}
                            </Select>
                        </Field>
                    </Col>
                    <Col span={24}>
                        <Field label="Measurable element text *">
                            <TextArea rows={4} value={meForm.question_text} onChange={e => setMeForm({ ...meForm, question_text: e.target.value })} />
                        </Field>
                    </Col>
                </Row>
            </Modal>

            {/* ME view modal */}
            <Modal title={<span>ME: <Tag color="blue">{meForm.ref_no}</Tag></span>} open={viewModal} onCancel={() => setViewModal(false)} footer={[<Button key="c" onClick={() => setViewModal(false)}>Close</Button>]} width={600}>
                <div style={{ fontSize: 14, lineHeight: 1.7, color: '#333' }}>{meForm.me_text || meForm.question_text}</div>
                <div style={{ marginTop: 16, display: 'flex', gap: 24 }}>
                    <span><strong>Ref:</strong> {meForm.ref_no || '—'}</span>
                    <span><strong>Verification:</strong> {meForm.verification_means}</span>
                    <span><strong>Scoring:</strong> {meForm.scoring_rule}</span>
                </div>
            </Modal>

            {/* Checklist editor modal */}
            <Modal
                title={<span>Checklist — <Tag color="blue">{checklistMe?.ref_no}</Tag></span>}
                open={checklistModal}
                onCancel={() => setChecklistModal(false)}
                onOk={saveChecklist}
                confirmLoading={checklistSaving}
                okText="Save Checklist"
                width={640}
                destroyOnClose
            >
                <div style={{ fontSize: 12, color: '#666', marginBottom: 14, padding: '8px 10px', background: '#fafafa', borderRadius: 6 }}>
                    {checklistMe?.me_text || checklistMe?.question_text}
                </div>

                <Alert
                    type={checklistReviewed ? 'success' : 'warning'}
                    showIcon
                    style={{ marginBottom: 14 }}
                    message={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: 12 }}>
                                {checklistReviewed
                                    ? 'Reviewed — assessors will see this checklist and get a suggested score.'
                                    : 'Not yet reviewed — assessors will NOT see this checklist until you mark it reviewed.'}
                            </span>
                            <Switch
                                checked={checklistReviewed}
                                onChange={setChecklistReviewed}
                                checkedChildren="Reviewed"
                                unCheckedChildren="Needs review"
                            />
                        </div>
                    }
                />

                {checklistItems.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: '#bbb', width: 18, flexShrink: 0, textAlign: 'right' }}>{idx + 1}.</span>
                        <Input
                            value={item.item_text}
                            onChange={e => updateChecklistItem(idx, e.target.value)}
                            placeholder="e.g. Documented Screening/Triaging SOP"
                        />
                        <Tooltip title="Move up">
                            <Button size="small" type="text" icon={<ArrowUpOutlined />}
                                disabled={idx === 0} onClick={() => moveChecklistItem(idx, -1)} />
                        </Tooltip>
                        <Tooltip title="Move down">
                            <Button size="small" type="text" icon={<ArrowDownOutlined />}
                                disabled={idx === checklistItems.length - 1} onClick={() => moveChecklistItem(idx, 1)} />
                        </Tooltip>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />}
                            onClick={() => removeChecklistItem(idx)} />
                    </div>
                ))}

                <Button type="dashed" block icon={<PlusOutlined />} onClick={addChecklistItem} style={{ marginTop: 4 }}>
                    Add item
                </Button>

                <div style={{ fontSize: 11, color: '#aaa', marginTop: 12 }}>
                    Blank items are ignored on save. Saving with zero items clears this ME's checklist entirely —
                    assessors will fall back to fully manual scoring.
                </div>
            </Modal>
        </div>
    );
}